import { useEffect, useRef, useState, useCallback } from 'react';
import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { OptimisticPreviewManager, AwarenessState, UserInfo } from '../lib/diagram/OptimisticPreviewManager';

interface UseOptimisticPreviewOptions {
  ydoc: Y.Doc | null;
  awareness: awarenessProtocol.Awareness | null;
  onElectricUpdate?: (shapeId: string) => void;
}

export function useOptimisticPreview({ 
  ydoc, 
  awareness,
  onElectricUpdate 
}: UseOptimisticPreviewOptions) {
  const managerRef = useRef<OptimisticPreviewManager | null>(null);
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    if (!ydoc || !awareness) {
      setIsReady(false);
      return;
    }
    
    const manager = new OptimisticPreviewManager(ydoc, awareness);
    managerRef.current = manager;
    setIsReady(true);
    
    // Listen for Electric updates on shapes (observeDeep to catch nested Y.Map property changes)
    const handleShapeUpdate = (events: Y.YEvent<any>[]) => {
      const changedIds = new Set<string>();

      for (const event of events) {
        if (event.path.length === 0 && event instanceof Y.YMapEvent) {
          event.changes.keys.forEach((change, key) => {
            if (change.action === 'update' || change.action === 'add') {
              changedIds.add(key);
            }
          });
        } else if (event.path.length >= 1) {
          changedIds.add(String(event.path[0]));
        }
      }

      for (const id of changedIds) {
        manager.onElectricUpdate(id);
        onElectricUpdate?.(id);
      }
    };

    const shapesMap = ydoc.getMap('shapes');
    shapesMap.observeDeep(handleShapeUpdate);

    return () => {
      shapesMap.unobserveDeep(handleShapeUpdate);
      manager.destroy();
      managerRef.current = null;
      setIsReady(false);
    };
  }, [ydoc, awareness, onElectricUpdate]);
  
  const updatePreview = useCallback((shapeId: string, changes: Record<string, any>) => {
    managerRef.current?.updatePreview(shapeId, changes);
  }, []);
  
  const getShapeState = useCallback(<T extends Record<string, any>>(
    shapeId: string, 
    baseState: T
  ): T => {
    return managerRef.current?.getShapeState(shapeId, baseState) || baseState;
  }, []);
  
  const setUserSelection = useCallback((shapeIds: string[]) => {
    managerRef.current?.setUserSelection(shapeIds);
  }, []);
  
  const forceCommit = useCallback(() => {
    managerRef.current?.forceCommit();
  }, []);
  
  const clearPreviewForShape = useCallback((shapeId: string) => {
    managerRef.current?.clearPreviewForShape(shapeId);
  }, []);
  
  return {
    isReady,
    updatePreview,
    getShapeState,
    setUserSelection,
    forceCommit,
    clearPreviewForShape,
    manager: managerRef.current
  };
}

export function useUserSelections(awareness: awarenessProtocol.Awareness | null) {
  const [selections, setSelections] = useState<Map<string, { user: UserInfo; selection: string[] }>>(
    new Map()
  );
  
  useEffect(() => {
    if (!awareness) return;
    
    const recompute = () => {
      const newSelections = new Map<string, { user: UserInfo; selection: string[] }>();
      const allStates = Array.from(awareness.getStates().entries()) as [number, AwarenessState][];
      
      for (const [clientId, state] of allStates) {
        if (state?.user && state?.selection?.length > 0) {
          newSelections.set(clientId.toString(), {
            user: state.user,
            selection: state.selection
          });
        }
      }
      
      setSelections(newSelections);
    };
    
    const onUpdate = ({ added, updated, removed }: { added: number[]; updated: number[]; removed: number[] }) => {
      const localId = awareness.clientID;
      const changed = [...(added || []), ...(updated || []), ...(removed || [])].filter((id) => id !== localId);
      if (changed.length === 0) {
        return; // ignore local-only changes to avoid re-render storms while dragging
      }
      recompute();
    };
    
    awareness.on('update', onUpdate);
    recompute(); // Initial state
    
    return () => {
      awareness.off('update', onUpdate);
    };
  }, [awareness]);
  
  return selections;
}

export function useShapeInteraction(
  shapeId: string,
  updatePreview: (shapeId: string, changes: Record<string, any>) => void,
  _forceCommit: () => void
) {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);
  
  const handleDragMove = useCallback((e: any) => {
    updatePreview(shapeId, {
      x: e.target.x(),
      y: e.target.y()
    });
  }, [shapeId, updatePreview]);
  
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    // Final position will be committed automatically via debounce
  }, []);
  
  const handleTransformStart = useCallback(() => {
    setIsResizing(true);
  }, []);
  
  const handleTransform = useCallback((e: any) => {
    const node = e.target;
    updatePreview(shapeId, {
      x: node.x(),
      y: node.y(),
      width: node.width() * node.scaleX(),
      height: node.height() * node.scaleY(),
      rotation: node.rotation()
    });
  }, [shapeId, updatePreview]);
  
  const handleTransformEnd = useCallback(() => {
    setIsResizing(false);
    // Reset scale after applying to width/height
    const node = event?.target as any;
    if (node) {
      node.scaleX(1);
      node.scaleY(1);
    }
  }, []);
  
  return {
    isDragging,
    isResizing,
    handlers: {
      onDragStart: handleDragStart,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
      onTransformStart: handleTransformStart,
      onTransform: handleTransform,
      onTransformEnd: handleTransformEnd
    }
  };
}